import { useMemo } from 'react';
import { omit } from 'ramda';
import { useCommonTranslation } from 'c-translation';

type Params = {
    advancedFilters: Record<string, string[]> | null | undefined;
    allValuesByField: Record<string, any> | null;
    resolvedType: 'digital' | 'classic' | null;
    isDsp: boolean;
    isAverageAudienceMode: boolean;
    dspAudienceFilterKey: string;
    dspNoAudienceValue: string;
    noAudienceUiValue: string;
    dspAverageImpactsLabel: string;
    impactsWord: string;
};

export const useCampaignAnalyticsDspState = ({
    advancedFilters,
    allValuesByField,
    resolvedType,
    isDsp,
    isAverageAudienceMode,
    dspAudienceFilterKey,
    dspNoAudienceValue,
    noAudienceUiValue,
    dspAverageImpactsLabel,
    impactsWord,
}: Params) => {
    const t = useCommonTranslation();

    const dspAudienceFilterValues = useMemo(
        () =>
            (advancedFilters?.[dspAudienceFilterKey] ?? advancedFilters?.audience_version ?? [])
                .filter(v => v != null && v !== '')
                .map(v => String(v)),
        [advancedFilters, dspAudienceFilterKey],
    );

    const selectedAudienceLabelForCharts = useMemo(() => {
        if (isAverageAudienceMode) return dspAverageImpactsLabel;
        if (!isDsp || dspAudienceFilterValues.length !== 1) return null;
        if (dspAudienceFilterValues[0] === dspNoAudienceValue) return null;
        return dspAudienceFilterValues[0];
    }, [
        dspAudienceFilterValues,
        dspAverageImpactsLabel,
        dspNoAudienceValue,
        isAverageAudienceMode,
        isDsp,
    ]);

    const fieldFilters = useMemo<Record<string, string[]>>(() => {
        const cloned: Record<string, string[]> = Object.fromEntries(
            Object.entries(advancedFilters ?? {}).map(([k, v]) => [
                k,
                Array.isArray(v) ? [...v] : [],
            ]),
        );

        if (resolvedType && !isDsp) {
            cloned.display_type = [resolvedType === 'digital' ? 'Display' : 'Classic'];
        }

        if (isDsp) {
            if (isAverageAudienceMode) {
                if (dspAudienceFilterValues.includes(dspNoAudienceValue)) {
                    cloned[dspAudienceFilterKey] = [dspNoAudienceValue];
                } else {
                    delete cloned[dspAudienceFilterKey];
                }
            } else if (dspAudienceFilterValues.length > 0) {
                cloned[dspAudienceFilterKey] = [...dspAudienceFilterValues];
            } else {
                delete cloned[dspAudienceFilterKey];
            }

            delete cloned.audience_version;
            delete cloned.scheduled;
        }

        return cloned;
    }, [
        advancedFilters,
        dspAudienceFilterKey,
        dspAudienceFilterValues,
        dspNoAudienceValue,
        isAverageAudienceMode,
        isDsp,
        resolvedType,
    ]);

    const impactAnalysisFieldFilters = useMemo<Record<string, string[]>>(() => {
        if (!isDsp) return fieldFilters;

        const available = (
            ((allValuesByField as any)?.[dspAudienceFilterKey] ??
                allValuesByField?.audience_version ??
                []) as Array<string | null | undefined>
        )
            .filter(v => v != null && v !== '' && v !== dspNoAudienceValue)
            .map(v => String(v));

        const withoutAudienceFilters = omit(
            [dspAudienceFilterKey, 'audience_version'],
            fieldFilters,
        ) as Record<string, string[]>;

        if (!available.length) return fieldFilters;

        return {
            ...withoutAudienceFilters,
            [dspAudienceFilterKey]: Array.from(new Set(available)),
        };
    }, [allValuesByField, dspAudienceFilterKey, dspNoAudienceValue, fieldFilters, isDsp]);

    const nonAuditedTotalFieldFilters = useMemo<Record<string, string[]>>(
        () =>
            (omit([dspAudienceFilterKey, 'audience_version'], fieldFilters) as Record<
                string,
                string[]
            >) ?? {},
        [dspAudienceFilterKey, fieldFilters],
    );

    const isNonAuditedAudienceOnly = useMemo(
        () =>
            isDsp &&
            dspAudienceFilterValues.length === 1 &&
            dspAudienceFilterValues[0] === dspNoAudienceValue,
        [dspAudienceFilterValues, dspNoAudienceValue, isDsp],
    );
    const isAllAudiencesDsp = useMemo(
        () => isDsp && !isAverageAudienceMode && dspAudienceFilterValues.length === 0,
        [dspAudienceFilterValues.length, isAverageAudienceMode, isDsp],
    );
    const hideImpressionAnalysis = useMemo(() => isAllAudiencesDsp, [isAllAudiencesDsp]);

    const audienceModeLabel = useMemo(() => {
        if (!isDsp) return '';
        if (isAverageAudienceMode) {
            return t('Modules.Main.Campaigns.Overview.Analytics.againstAudienceLabel', {
                value: dspAverageImpactsLabel,
            });
        }

        const selected = advancedFilters?.[dspAudienceFilterKey] ?? [];
        const hasNonAudited = selected.includes(dspNoAudienceValue);
        const hasAudited = selected.some(
            v => v != null && String(v).trim() !== '' && v !== dspNoAudienceValue,
        );

        if (!hasAudited && !hasNonAudited) return '';
        if (hasAudited && hasNonAudited) return '';
        if (hasAudited && selectedAudienceLabelForCharts) {
            return t('Modules.Main.Campaigns.Overview.Analytics.againstAudienceLabel', {
                value: `${selectedAudienceLabelForCharts} ${impactsWord}`,
            });
        }

        return '(non-audited)';
    }, [
        advancedFilters,
        dspAudienceFilterKey,
        dspAverageImpactsLabel,
        dspNoAudienceValue,
        impactsWord,
        isAverageAudienceMode,
        isDsp,
        selectedAudienceLabelForCharts,
        t,
    ]);

    const allValuesByFieldForUi = useMemo(
        () => (isDsp ? (omit(['scheduled'], allValuesByField ?? {}) as any) : allValuesByField),
        [allValuesByField, isDsp],
    );

    const selectedFiltersForUi = useMemo(
        () => (isDsp ? (omit(['scheduled'], advancedFilters ?? {}) as any) : advancedFilters),
        [advancedFilters, isDsp],
    );

    const audienceVersionOptions = useMemo(() => {
        const raw = ((allValuesByField as any)?.[dspAudienceFilterKey] ??
            allValuesByField?.audience_version ??
            []) as Array<string | null | undefined>;
        const normalized = raw.filter(v => v != null).map(v => String(v));
        const unique = Array.from(new Set(normalized));

        return {
            hasNoAudience: unique.includes(dspNoAudienceValue),
            versions: unique.filter(v => v !== dspNoAudienceValue),
        };
    }, [allValuesByField, dspAudienceFilterKey, dspNoAudienceValue]);

    const audienceUiValue = useMemo(() => {
        const value = advancedFilters?.[dspAudienceFilterKey]?.[0];

        if (!advancedFilters?.[dspAudienceFilterKey]?.length) return '';
        if (value === dspNoAudienceValue) return noAudienceUiValue;
        return value ?? '';
    }, [advancedFilters, dspAudienceFilterKey, dspNoAudienceValue, noAudienceUiValue]);

    const impressionAnalysisVarianceContextLabel = useMemo(() => {
        if (isAverageAudienceMode) {
            return t('Modules.Main.Campaigns.Overview.Analytics.average');
        }

        if (selectedAudienceLabelForCharts) {
            return t('Modules.Main.Campaigns.Overview.Analytics.auditedWithValue', {
                value: selectedAudienceLabelForCharts,
            });
        }

        return t('Modules.Main.Campaigns.Overview.Analytics.audited');
    }, [isAverageAudienceMode, selectedAudienceLabelForCharts, t]);

    const impactAnalysisAudienceLabel = useMemo(() => {
        if (isAverageAudienceMode) return null;

        const audiences = (impactAnalysisFieldFilters?.[dspAudienceFilterKey] ?? []).filter(
            value => value != null && value !== '' && value !== dspNoAudienceValue,
        );

        if (!audiences.length) return null;
        if (audiences.length === 1) return audiences[0];
        return audiences.join(', ');
    }, [
        dspAudienceFilterKey,
        dspNoAudienceValue,
        impactAnalysisFieldFilters,
        isAverageAudienceMode,
    ]);

    const audienceButtonLabel = useMemo(() => {
        if (isAverageAudienceMode) {
            return t('Modules.Main.Campaigns.Overview.Analytics.audienceAverages');
        }
        if (audienceUiValue === '') {
            return t('Modules.Main.Campaigns.Overview.Analytics.audienceAll');
        }
        if (audienceUiValue === noAudienceUiValue) {
            return t('Modules.Main.Campaigns.Overview.Analytics.audienceNone');
        }
        return String(audienceUiValue);
    }, [audienceUiValue, isAverageAudienceMode, noAudienceUiValue, t]);

    return {
        dspAudienceFilterValues,
        selectedAudienceLabelForCharts,
        fieldFilters,
        impactAnalysisFieldFilters,
        nonAuditedTotalFieldFilters,
        isNonAuditedAudienceOnly,
        hideImpressionAnalysis,
        audienceModeLabel,
        allValuesByFieldForUi,
        selectedFiltersForUi,
        audienceVersionOptions,
        audienceUiValue,
        audienceButtonLabel,
        impressionAnalysisVarianceContextLabel,
        impactAnalysisAudienceLabel,
    };
};
